import React, { useContext, useEffect, useMemo } from 'react';
import { Box, Button, Container, TextField as MUITextField, Typography, Divider } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery } from '@tanstack/react-query';
import TextField from '../components/TextField';
import { UserContext } from '../context/UserContext';
import { getBillingDetail, useCreateBillingDetail, useUpdateBillingDetail } from '../queries/billingDetail';
import SelectField from '../components/SelectField';
import { toast } from 'react-toastify';
import { validateIBANOrBankAccount, formatIBANForDisplay, formatIBANForServer } from '../utils/helper';

const euCountries = [
  'Austria',
  'Belgium',
  'Bulgaria',
  'Croatia',
  'Cyprus',
  'Czech Republic',
  'Denmark',
  'Estonia',
  'Finland',
  'France',
  'Germany',
  'Greece',
  'Hungary',
  'Ireland',
  'Italy',
  'Latvia',
  'Lithuania',
  'Luxembourg',
  'Malta',
  'Netherlands',
  'Poland',
  'Portugal',
  'Romania',
  'Slovakia',
  'Slovenia',
  'Spain',
  'Sweden',
].sort();

const nonEuEurope = [
  'Albania',
  'Bosnia and Herzegovina',
  'Iceland',
  'Kosovo',
  'Moldova',
  'Montenegro',
  'North Macedonia',
  'Norway',
  'Russia',
  'Serbia',
  'Switzerland',
  'Turkey',
  'Ukraine',
  'United Kingdom',
].sort();

const northAmerica = ['Canada', 'Mexico', 'United States'].sort();

const otherCountries = [
  'Argentina',
  'Australia',
  'Brazil',
  'China',
  'Egypt',
  'India',
  'Indonesia',
  'Israel',
  'Japan',
  'Malaysia',
  'New Zealand',
  'Philippines',
  'Saudi Arabia',
  'Singapore',
  'South Africa',
  'South Korea',
  'Thailand',
  'United Arab Emirates',
].sort();

const bicOrRoutingRegex = /^(?:[A-Z]{6}[A-Z0-9]{2}(?:[A-Z0-9]{3})?|\d{9})$/i;

const schema = z
  .object({
    firstName: z.string().min(1, 'First name is required'),
    lastName: z.string().min(1, 'Last name is required'),
    companyName: z
      .string()
      .transform((val) => {
        // Trim whitespace - if only spaces, treat as empty
        if (!val) return '';
        const trimmed = val.trim();
        return trimmed === '' ? '' : trimmed;
      }),
    address: z.string().min(1, 'Address Line 1 is required'),
    addressLine2: z.string().optional(),
    city: z.string().min(1, 'City is required'),
    state: z.string().optional(),
    zip: z.string().min(1, 'Postal / ZIP Code is required'),
    country: z.string().min(1, 'Country is required'),
    vat: z.string().optional(),
    companyRegistrationNumber: z.string().optional(),
    iban: z.string().optional(),
    bic: z.string().optional(),
    bankName: z.string().optional(),
    bankAddressLine1: z.string().optional(),
    bankAddressLine2: z.string().optional(),
    bankCity: z.string().optional(),
    bankState: z.string().optional(),
    bankCountry: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    const countryNormalized = (data.country || '').trim().toLowerCase();
    const isNetherlands = countryNormalized === 'netherlands' || countryNormalized === 'the netherlands';
    const stateRequiredCountries = ['united states', 'canada', 'australia'];
    const isStateRequiredCountry = stateRequiredCountries.includes(countryNormalized);
    
    // Use trimmed value for hasCompany check
    const trimmedCompanyName = data.companyName ? data.companyName.trim() : '';
    const hasCompany = !!trimmedCompanyName;
    
    // VAT validation: required for Netherlands + company
    const shouldRequireVAT = isNetherlands && hasCompany;
    if (shouldRequireVAT && !data.vat) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['vat'],
        message: 'VAT / Company Tax ID is required for Dutch companies.',
      });
    }
    
    // State/Region validation: required for US, CA, AU
    if (isStateRequiredCountry && !data.state) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['state'],
        message: 'State / Region is required for United States, Canada, or Australia.',
      });
    }
    
    // Bank Details validation: conditional based on country
    if (!isNetherlands) {
      // IBAN is always required
      if (!data.iban) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['iban'],
          message: 'IBAN / Bank Account Number is required.',
        });
      } else if (!validateIBANOrBankAccount(data.iban)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['iban'],
          message: 'Invalid IBAN / Bank Account Number format. Please enter a valid IBAN or bank account number.',
        });
      }
      
      // BIC required for non-Netherlands
      if (!data.bic) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['bic'],
          message: 'BIC / SWIFT / Routing Code is required for non-Dutch countries.',
        });
      } else if (!bicOrRoutingRegex.test((data.bic || '').trim())) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['bic'],
          message: 'Invalid BIC / SWIFT / Routing Code format.',
        });
      }
      
      // Bank Name required for non-Netherlands
      if (!data.bankName) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['bankName'],
          message: 'Bank Name is required for non-Dutch countries.',
        });
      }
      
      // Bank Address Line 1 required for non-Netherlands
      if (!data.bankAddressLine1) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['bankAddressLine1'],
          message: 'Bank Address Line 1 is required for non-Dutch countries.',
        });
      }
      
      // Bank City required for non-Netherlands
      if (!data.bankCity) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['bankCity'],
          message: 'Bank City is required for non-Dutch countries.',
        });
      }
      
      // Bank Country required for non-Netherlands
      if (!data.bankCountry) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['bankCountry'],
          message: 'Bank Country is required for non-Dutch countries.',
        });
      }
      
      // Bank State required for US, CA, AU when country ≠ Netherlands
      if (isStateRequiredCountry && !data.bankState) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['bankState'],
          message: 'Bank State / Region is required for United States, Canada, or Australia.',
        });
      }
    } else {
      // For Netherlands: IBAN is required, BIC is optional
      if (!data.iban) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['iban'],
          message: 'IBAN / Bank Account Number is required.',
        });
      } else if (!validateIBANOrBankAccount(data.iban)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['iban'],
          message: 'Invalid IBAN / Bank Account Number format. Please enter a valid IBAN or bank account number.',
        });
      }
      
      // BIC is optional for Netherlands, but if provided, must be valid
      if (data.bic && !bicOrRoutingRegex.test((data.bic || '').trim())) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['bic'],
          message: 'Invalid BIC / SWIFT / Routing Code format.',
        });
      }
    }
  });

const Settings = () => {
  const { user, fetchUser } = useContext(UserContext);
  const { data: billingDetailData, isLoading, isRefetching, refetch } = useQuery(getBillingDetail({}));

  const { mutate: createBillingDetail } = useCreateBillingDetail();
  const { mutate: updateBillingDetail } = useUpdateBillingDetail();

  const methods = useForm({
    resolver: zodResolver(schema),
    reValidateMode: 'onChange',
    mode: 'all',
    defaultValues: {
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      companyName: billingDetailData?.companyName || '',
      address: billingDetailData?.address || '',
      addressLine2: billingDetailData?.addressLine2 || '',
      city: billingDetailData?.city || '',
      state: billingDetailData?.state || '',
      zip: billingDetailData?.zip || '',
      country: billingDetailData?.country || '',
      vat: billingDetailData?.vat || '',
      companyRegistrationNumber: billingDetailData?.companyRegistrationNumber || '',
      iban: formatIBANForDisplay(billingDetailData?.iban || ''),
      bic: billingDetailData?.bic || '',
      bankName: billingDetailData?.bankName || '',
      bankAddressLine1: billingDetailData?.bankAddressLine1 || '',
      bankAddressLine2: billingDetailData?.bankAddressLine2 || '',
      bankCity: billingDetailData?.bankCity || '',
      bankState: billingDetailData?.bankState || '',
      bankCountry: billingDetailData?.bankCountry || '',
    },
  });

  const {
    handleSubmit,
    getValues,
    formState: { isValid },
    reset,
    watch,
  } = methods;

  const countryNormalized = (watch('country') || '').trim().toLowerCase();
  const isNetherlands = countryNormalized === 'netherlands' || countryNormalized === 'the netherlands';
  // Trim companyName for conditional logic - treat spaces-only as empty
  const companyNameValue = watch('companyName') || '';
  const trimmedCompanyName = companyNameValue.trim();
  const hasCompany = !!trimmedCompanyName;
  const isVatRequired = isNetherlands && hasCompany;
  
  // State/Region required for US, CA, AU
  const stateRequiredCountries = ['united states', 'canada', 'australia'];
  const isStateRequiredCountry = stateRequiredCountries.includes(countryNormalized);

  const allCountries = useMemo(() => {
    const getOption = (value) => ({ value, label: value });
    const getSubheader = (value) => ({ value, label: value, isSubheader: true });
    return [
      getSubheader('EU Countries'),
      ...euCountries.map(getOption),
      getSubheader('Non-EU Europe'),
      ...nonEuEurope.map(getOption),
      getSubheader('North America'),
      ...northAmerica.map(getOption),
      getSubheader('Other'),
      ...otherCountries.map(getOption),
    ];
  }, []);

  useEffect(() => {
    if (!isLoading && !isRefetching && billingDetailData && user) {
      reset(
        {
          firstName: user.firstName || '',
          lastName: user.lastName || '',
          companyName: billingDetailData.companyName || '',
          address: billingDetailData.address || '',
          addressLine2: billingDetailData.addressLine2 || '',
          city: billingDetailData.city || '',
          state: billingDetailData.state || '',
          zip: billingDetailData.zip || '',
          country: billingDetailData.country || '',
          vat: billingDetailData.vat || '',
          companyRegistrationNumber: billingDetailData.companyRegistrationNumber || '',
          iban: formatIBANForDisplay(billingDetailData.iban || ''),
          bic: billingDetailData.bic || '',
          bankName: billingDetailData.bankName || '',
          bankAddressLine1: billingDetailData.bankAddressLine1 || '',
          bankAddressLine2: billingDetailData.bankAddressLine2 || '',
          bankCity: billingDetailData.bankCity || '',
          bankState: billingDetailData.bankState || '',
          bankCountry: billingDetailData.bankCountry || '',
        },
        { keepDirty: false },
      );
    }
  }, [isLoading, isRefetching, billingDetailData, user, reset, getValues]);

  const onSubmit = (data) => {
    // Ensure companyName is trimmed - treat spaces-only as empty
    // IBAN: standard IBAN → uppercase, no spaces for server; non-IBAN → trimmed
    const processedData = {
      ...data,
      companyName: data.companyName ? data.companyName.trim() || '' : '',
      iban: formatIBANForServer(data.iban),
    };
    
    if (!billingDetailData) {
      createBillingDetail(
        { data: processedData },
        {
          onSuccess: () => {
            refetch();
            fetchUser();
            toast('Created billing details successfully!', { type: 'success' });
          },
          onError: (error) => {
            const message = error?.response?.data?.error || error?.message || 'Failed to create billing details.';
            toast(message, { type: 'error' });
          },
        },
      );
    } else {
      updateBillingDetail(
        { data: processedData },
        {
          onSuccess: () => {
            refetch();
            fetchUser();
            toast('Updated billing details successfully!', { type: 'success' });
          },
          onError: (error) => {
            const message = error?.response?.data?.error || error?.message || 'Failed to update billing details.';
            toast(message, { type: 'error' });
          },
        },
      );
    }
  };

  return (
    <Container maxWidth='sm' sx={{ my: 4 }}>
      <Typography variant='h4' gutterBottom>
        Billing Details
      </Typography>
      <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
        We use self-billing. You don't need to send an invoice; we create it for you.
      </Typography>
      {!isLoading && (
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Box display='flex' flexDirection='column' gap={3}>
              {/* Section 1: Account */}
              <Box>
                <Typography variant='h6' gutterBottom fontWeight='medium'>
                  Account
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Box display='flex' flexDirection='column' gap={2}>
                  <TextField
                    label='Company Name'
                    name='companyName'
                    helperText="Leave empty if you're claiming as a natural person and not as a company."
                  />
                  <Box display='flex' gap={2}>
                    <TextField label='First Name' name='firstName' required />
                    <TextField label='Last Name' name='lastName' required />
                  </Box>
                  <MUITextField label='Email' disabled value={user.email} fullWidth />
                </Box>
                <Divider sx={{ mt: 2 }} />
              </Box>

              {/* Section 2: Address */}
              <Box>
                <Typography variant='h6' gutterBottom fontWeight='medium'>
                  Address
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Box display='flex' flexDirection='column' gap={2}>
                  <TextField label='Address Line 1' name='address' required />
                  <TextField label='Address Line 2' name='addressLine2' />
                  <TextField label='Postal / ZIP Code' name='zip' required />
                  <TextField label='City' name='city' required />
                  {isStateRequiredCountry && (
                    <TextField label='State / Region' name='state' required />
                  )}
                  <SelectField label='Country' name='country' options={allCountries} required />
                </Box>
                <Divider sx={{ mt: 2 }} />
              </Box>

              {/* Section 3: Business IDs */}
              {hasCompany && (
                <Box>
                  <Typography variant='h6' gutterBottom fontWeight='medium'>
                    Business IDs
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Box display='flex' flexDirection='column' gap={2}>
                    <Box>
                      <TextField
                        label='VAT / Company Tax ID'
                        name='vat'
                        required={isVatRequired}
                        helperText={
                          hasCompany
                            ? "Shown because you entered a company name above. Please enter your VAT or local company tax number."
                            : ''
                        }
                      />
                    </Box>
                    <Box>
                      <TextField
                        label='Company Registration Number (CoC)'
                        name='companyRegistrationNumber'
                        helperText="Enter your business registration number (e.g., CoC, KVK, EIN, or SIRET)."
                      />
                    </Box>
                  </Box>
                  <Divider sx={{ mt: 2 }} />
                </Box>
              )}

              {/* Section 4: Bank Details */}
              <Box>
                <Typography variant='h6' gutterBottom fontWeight='medium'>
                  Bank Details
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Box display='flex' flexDirection='column' gap={2}>
                  {isNetherlands ? (
                    <TextField
                      label='IBAN / Bank Account Number'
                      name='iban'
                      required
                      helperText='We accept an IBAN (e.g. EU, UK) or a standard bank account number (e.g. US, Canada). IBANs are automatically validated.'
                    />
                  ) : (
                    <>
                      <TextField
                        label='IBAN / Bank Account Number'
                        name='iban'
                        required
                        helperText='We accept an IBAN (e.g. EU, UK) or a standard bank account number (e.g. US, Canada). IBANs are automatically validated.'
                      />
                      <TextField label='Bank Name' name='bankName' required />
                      <TextField label='BIC / SWIFT / Routing Code' name='bic' required />
                      <TextField label='Bank Address Line 1' name='bankAddressLine1' required />
                      <TextField label='Bank Address Line 2' name='bankAddressLine2' />
                      <TextField label='Bank City' name='bankCity' required />
                      {isStateRequiredCountry && (
                        <TextField label='Bank State / Region' name='bankState' required />
                      )}
                      <SelectField label='Bank Country' name='bankCountry' options={allCountries} required />
                      <Typography variant='caption' color='text.secondary'>
                        Please add your bank's full address so we can process international payments.
                      </Typography>
                    </>
                  )}
                </Box>
                <Divider sx={{ mt: 2 }} />
              </Box>

              {/* Section 5: Action */}
              <Box>
                <Button type='submit' variant='contained' color='primary' disabled={!isValid} fullWidth>
                  Save
                </Button>
                <Divider sx={{ mt: 2 }} />
              </Box>
            </Box>
          </form>
        </FormProvider>
      )}
    </Container>
  );
};

export default Settings;
